import { useContext, useMemo, useState } from "react";
import styles from "./BundleUpsells.module.css";
import OrderContext from "~/contexts/OrderContext";
import MenuContext from "~/contexts/MenuContext";
import TaxContext from "~/contexts/TaxContext";
import ProductModal from "~/modules/order/containers/ProductModal/ProductModal";
import { CartLine } from "~/entities/orderEntities";
import { MenuTabItem } from "~/entities/menuEntities";
import { generateLine, choiceRequiredForProduct } from "~/helpers/cartHelpers";
import { getProductsList } from "~/helpers/menuHelpers";

// Server-computed "complete this bundle" strip (deep-dive §4.4). Renders
// orderSummary.bundleUpsells; the add flow follows AssetModal's pattern:
// generateLine → open the nested ProductModal when the product needs child
// choices, otherwise add the line directly.
const BundleUpsells = () => {
  const { orderSummary, addOrderLine, readonlyCart, orderSource } =
    useContext(OrderContext);
  const { menu } = useContext(MenuContext);
  const { formatAmount } = useContext(TaxContext);

  const [addingLine, setAddingLine] = useState<CartLine | null>(null);

  const upsells = orderSummary.bundleUpsells ?? [];

  const menuItemByProductCode = useMemo(() => {
    const map = new Map<string, MenuTabItem>();
    if (menu) {
      for (const item of getProductsList(menu)) {
        map.set(item.productCode, item);
      }
    }
    return map;
  }, [menu]);

  if (readonlyCart || !upsells.length) {
    return null;
  }

  const addToCart = (menuItem: MenuTabItem) => {
    if (!menu) {
      return;
    }
    const newCartLine = generateLine(menuItem, menu.code);
    if (choiceRequiredForProduct(menuItem.productData, orderSource)) {
      setAddingLine(newCartLine);
    } else {
      addOrderLine(newCartLine);
    }
  };

  return (
    <div className={styles.wrapper}>
      <ProductModal
        isInsert
        initLine={addingLine}
        onClose={() => setAddingLine(null)}
      />
      {upsells.map((upsell) => {
        // Only offer the Add button when the missing product is actually in
        // the loaded menu — an off-menu add would be deleted by the server's
        // menu-membership check on the next reprice (deep-dive §2.4).
        const menuItem = menuItemByProductCode.get(
          upsell.missingProductCodes[0]
        );
        return (
          <div key={upsell.promotionCode} className={styles.row}>
            <div className={styles.info}>
              <span className={styles.label}>Add {upsell.name}</span>
              <span className={styles.saving}>
                save {formatAmount(upsell.saving)}
              </span>
            </div>
            {menuItem && (
              <button
                type="button"
                className={styles.addButton}
                onClick={() => addToCart(menuItem)}
              >
                Add {formatAmount(upsell.completionCost)}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default BundleUpsells;
